import type { GuidanceSection } from '@/lib/schema/types';

/**
 * Renders the manual's "Administration of the Scale" / "Scoring System"
 * guidance (pp. 9-11) — numbered protocol points, the Observer Guidelines
 * bullet list, and the seven rating rules, as published.
 *
 * Shown inside the help dialog so observers read the actual protocol rather
 * than our paraphrase of it.
 */
export function AdministrationSections({
  sections,
}: {
  sections: GuidanceSection[];
}) {
  return (
    <>
      {sections.map((sec) => (
        <section key={sec.heading}>
          <h3 className="font-serif text-base font-semibold text-dece-navy">
            {sec.heading}
          </h3>
          {sec.source_pages.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              FCCERS-3 manual, p.{sec.source_pages.join(', p.')}
            </p>
          )}
          <div className="mt-2 space-y-2">
            {sec.blocks.map((b, i) => {
              if (b.kind === 'subheading') {
                return (
                  <h4
                    key={i}
                    className="pt-1 font-semibold italic text-foreground"
                  >
                    {b.text}
                  </h4>
                );
              }
              if (b.kind === 'bullets') {
                return (
                  <ul
                    key={i}
                    className="ml-1 list-disc space-y-1 pl-4 text-foreground/90"
                  >
                    {(b.items ?? []).map((it, j) => (
                      <li key={j}>{it}</li>
                    ))}
                  </ul>
                );
              }
              if (b.kind === 'numbered') {
                return (
                  <div key={i} className="flex gap-2 text-foreground/90">
                    <span className="min-w-[1.4rem] font-semibold text-dece-navy">
                      {b.number}.
                    </span>
                    <p className="whitespace-pre-line">{b.text}</p>
                  </div>
                );
              }
              return (
                <p key={i} className="whitespace-pre-line text-foreground/90">
                  {b.text}
                </p>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}
